import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import type { Job } from "./JobModal";
import UploadField from "@/components/base/UploadField";
import ModuleGate from "@/components/base/ModuleGate";
import PhotoCard from "../detail/components/PhotoCard";
import { deleteFileFromVps } from "@/lib/vpsStorage";

/* ─── Types ──────────────────────────────────────────────────── */
export type JobPhoto = {
  id: string;
  tenant_id: string;
  job_id: string;
  client_id: string;
  photo_url: string;
  thumbnail_url: string | null;
  filename: string | null;
  category: string | null;
  is_selected: boolean;
  is_favorite: boolean;
  selection_note: string | null;
  selection_reviewed: boolean;
  selection_reviewed_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type InnerTab = "all" | "selected" | "favorites" | "notes";

interface Props {
  job: Job;
  tenantId: string;
  clientName: string;
  onClose: () => void;
}

/* ─── Helpers ───────────────────────────────────────────────── */
function downloadCSV(rows: { filename: string; url: string; category: string | null; selected: string; favorite: string; note: string }[], name: string) {
  const headers = ["Filename", "URL", "Category", "Selected", "Favorite", "Note"];
  const lines = rows.map((r) => [
    `"${r.filename.replace(/"/g, '""')}"`,
    r.url,
    r.category ?? "",
    r.selected,
    r.favorite,
    `"${r.note.replace(/"/g, '""')}"`,
  ]);
  const csv = [headers.join(","), ...lines.map((l) => l.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}_photo_selection.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ─── TabButton ─────────────────────────────────────────────── */
function TabButton({
  active,
  label,
  badge,
  onClick,
}: {
  active: boolean;
  label: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-[#C8A96E] text-black"
          : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
      }`}
    >
      {label}
      {typeof badge === "number" && badge > 0 && (
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
            active ? "bg-black/10 text-black/70" : "bg-slate-200 text-slate-500"
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/* ─── AddPhotoRow ───────────────────────────────────────────── */
function AddPhotoRow({
  onAdd,
  saving,
  tenantId,
  jobId,
}: {
  onAdd: (url: string, filename: string, category: string, fileId?: string) => void;
  saving: boolean;
  tenantId: string;
  jobId: string;
}) {
  const [url, setUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [category, setCategory] = useState("");
  const [pendingFileId, setPendingFileId] = useState<string | undefined>(undefined);

  const handleSubmit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    onAdd(trimmed, filename.trim(), category.trim(), pendingFileId);
    setUrl("");
    setFilename("");
    setCategory("");
    setPendingFileId(undefined);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm">
      <div className="flex items-center justify-between mb-1">
        <p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Add Photo</p>
        <span className="text-[10px] text-slate-400 font-mono">Upload or paste URL</span>
      </div>

      <UploadField
        label=""
        value={url}
        onChange={setUrl}
        accept="image/*"
        hint="JPG, PNG, WEBP — up to 50 MB"
        urlPlaceholder="https://example.com/photo.jpg"
        showPreview
        tenantId={tenantId}
        entityType="job_photo"
        entityId={jobId}
        folder="jobs"
        maxSizeMB={50}
        allowedTypes={["image/jpeg", "image/png", "image/webp", "image/jpg"]}
        onUploaded={(_url, meta) => setPendingFileId(meta.fileId)}
      />

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          placeholder="Filename (optional)"
          className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
        />
        <input
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Category (optional)"
          className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
        />
        <button
          onClick={handleSubmit}
          disabled={!url.trim() || saving}
          className="flex items-center gap-1.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              Add
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/* ─── Main Modal ────────────────────────────────────────────── */
export default function PhotosModal({ job, tenantId, clientName, onClose }: Props) {
  const [photos, setPhotos] = useState<JobPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [tab, setTab] = useState<InnerTab>("all");

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("job_photos")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b", confirmButtonColor: "#C8A96E" });
    } else {
      setPhotos((data as JobPhoto[]) ?? []);
    }
    setLoading(false);
  }, [job.id, tenantId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const handleAdd = async (url: string, filename: string, category: string, fileId?: string) => {
    setAdding(true);
    const payload = {
      tenant_id: tenantId,
      job_id: job.id,
      client_id: job.client_id,
      photo_url: url,
      thumbnail_url: null,
      filename: filename || null,
      category: category || null,
      is_selected: false,
      is_favorite: false,
      selection_note: null,
      sort_order: photos.length,
    };
    const { data, error } = await supabase
      .from("job_photos")
      .insert(payload)
      .select()
      .maybeSingle();

    if (error) {
      // If DB insert failed, try to clean up the uploaded file on VPS
      if (fileId) {
        try {
          await deleteFileFromVps({ fileId, tenantId, silent: true });
        } catch {
          // ignore cleanup failure
        }
      }
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b", confirmButtonColor: "#C8A96E" });
    } else if (data) {
      setPhotos((prev) => [...prev, data as JobPhoto]);
    }
    setAdding(false);
  };

  const handleToggleSelected = async (photo: JobPhoto) => {
    setBusyId(photo.id);
    const { data, error } = await supabase
      .from("job_photos")
      .update({ is_selected: !photo.is_selected, updated_at: new Date().toISOString() })
      .eq("id", photo.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b", confirmButtonColor: "#C8A96E" });
    } else if (data) {
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? (data as JobPhoto) : p)));
    }
    setBusyId(null);
  };

  const handleToggleFavorite = async (photo: JobPhoto) => {
    setBusyId(photo.id);
    const { data, error } = await supabase
      .from("job_photos")
      .update({ is_favorite: !photo.is_favorite, updated_at: new Date().toISOString() })
      .eq("id", photo.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b", confirmButtonColor: "#C8A96E" });
    } else if (data) {
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? (data as JobPhoto) : p)));
    }
    setBusyId(null);
  };

  const handleDelete = async (photo: JobPhoto) => {
    const result = await Swal.fire({
      title: "Delete Photo?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    setBusyId(photo.id);

    // Delete from VPS if file exists
    const { data: fileRecord } = await supabase
      .from("uploaded_files")
      .select("id")
      .eq("file_url", photo.photo_url)
      .eq("tenant_id", tenantId)
      .maybeSingle();
    if (fileRecord?.id) {
      try {
        await deleteFileFromVps({ fileId: fileRecord.id, tenantId, silent: true });
      } catch {
        // Continue even if VPS delete fails
      }
    }

    const { error } = await supabase
      .from("job_photos")
      .delete()
      .eq("id", photo.id)
      .eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b", confirmButtonColor: "#C8A96E" });
    } else {
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    }
    setBusyId(null);
  };

  const handleMove = async (photo: JobPhoto, direction: "up" | "down") => {
    const idx = photos.findIndex((p) => p.id === photo.id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= photos.length) return;

    const swapPhoto = photos[swapIdx];
    setBusyId(photo.id);

    const { error: err1 } = await supabase
      .from("job_photos")
      .update({ sort_order: swapPhoto.sort_order, updated_at: new Date().toISOString() })
      .eq("id", photo.id)
      .eq("tenant_id", tenantId);
    const { error: err2 } = await supabase
      .from("job_photos")
      .update({ sort_order: photo.sort_order, updated_at: new Date().toISOString() })
      .eq("id", swapPhoto.id)
      .eq("tenant_id", tenantId);

    if (!err1 && !err2) {
      setPhotos((prev) => {
        const next = [...prev];
        const a = { ...next[idx], sort_order: swapPhoto.sort_order };
        const b = { ...next[swapIdx], sort_order: photo.sort_order };
        next[idx] = a;
        next[swapIdx] = b;
        return next;
      });
    } else {
      fetchPhotos();
    }
    setBusyId(null);
  };

  const handleSaveEdit = async (photo: JobPhoto, filename: string, category: string) => {
    setBusyId(photo.id);
    const { data } = await supabase
      .from("job_photos")
      .update({
        filename: filename.trim() || null,
        category: category.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", photo.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (data) {
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? (data as JobPhoto) : p)));
    }
    setBusyId(null);
  };

  const handleMarkReviewed = async () => {
    const unreviewed = photos.filter((p) => p.is_selected && !p.selection_reviewed);
    if (unreviewed.length === 0) {
      Swal.fire({
        title: "All selected photos already reviewed",
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        color: "#1e293b",
      });
      return;
    }
    const result = await Swal.fire({
      title: "Mark selection as reviewed?",
      text: `This will mark ${unreviewed.length} selected photo(s) as reviewed.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Mark Reviewed",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    const ids = unreviewed.map((p) => p.id);
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("job_photos")
      .update({ selection_reviewed: true, selection_reviewed_at: now })
      .in("id", ids)
      .eq("tenant_id", tenantId);
    if (!error) {
      setPhotos((prev) =>
        prev.map((p) =>
          ids.includes(p.id) ? { ...p, selection_reviewed: true, selection_reviewed_at: now } : p
        )
      );
      Swal.fire({
        title: "Reviewed!",
        text: `${unreviewed.length} photo(s) marked as reviewed.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        color: "#1e293b",
        iconColor: "#C8A96E",
      });
    }
  };

  const handleExportCSV = () => {
    const rows = filtered.map((p) => ({
      filename: p.filename ?? "Untitled",
      url: p.photo_url,
      category: p.category,
      selected: p.is_selected ? "Yes" : "No",
      favorite: p.is_favorite ? "Yes" : "No",
      note: p.selection_note ?? "",
    }));
    if (rows.length === 0) return;
    const safeName = clientName.replace(/\s+/g, "_") ?? "job";
    downloadCSV(rows, `${safeName}_${String(job.id).slice(0, 8)}`);
  };

  const handleCopySelectedUrls = () => {
    const urls = filtered
      .filter((p) => p.is_selected)
      .map((p) => p.photo_url)
      .join("\n");
    if (!urls) {
      Swal.fire({
        title: "No selected photos to copy",
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        color: "#1e293b",
      });
      return;
    }
    navigator.clipboard.writeText(urls).then(() => {
      Swal.fire({
        title: "Copied!",
        text: "Selected photo URLs copied to clipboard.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        color: "#1e293b",
        iconColor: "#C8A96E",
      });
    });
  };

  /* ── Derived data ──────────────────────────────────────────── */
  const filtered = photos.filter((p) => {
    if (tab === "selected") return p.is_selected;
    if (tab === "favorites") return p.is_favorite;
    if (tab === "notes") return !!p.selection_note && p.selection_note.trim().length > 0;
    return true;
  });

  const totalCount = photos.length;
  const selectedCount = photos.filter((p) => p.is_selected).length;
  const favoriteCount = photos.filter((p) => p.is_favorite).length;
  const notesCount = photos.filter(
    (p) => p.selection_note && p.selection_note.trim().length > 0
  ).length;
  const reviewedCount = photos.filter((p) => p.is_selected && p.selection_reviewed).length;
  const selectedWithNotes = photos.filter(
    (p) => p.is_selected && p.selection_note && p.selection_note.trim().length > 0
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-5xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-4 h-4 flex items-center justify-center text-[#C8A96E]">
                <i className="ri-image-2-line text-sm"></i>
              </span>
              <p className="text-slate-400 text-xs uppercase tracking-wider">Photo Gallery</p>
            </div>
            <h2 className="text-slate-800 font-semibold text-base truncate">{job.title}</h2>
            <p className="text-slate-400 text-xs mt-0.5">{clientName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Stats row */}
        <div className="px-6 py-3 border-b border-slate-200 shrink-0 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="font-medium">{totalCount} total</span>
            <span className="text-[#C8A96E] font-semibold">{selectedCount} selected</span>
            <span className="text-red-400 font-semibold">{favoriteCount} favorites</span>
            <span className="text-slate-400">{reviewedCount}/{selectedCount} reviewed</span>
            {notesCount > 0 && <span className="text-slate-400">{notesCount} with notes</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopySelectedUrls}
              className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#C8A96E]/50 hover:bg-amber-50 text-slate-600 text-xs font-medium px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-link"></i>
              Copy URLs
            </button>
            <button
              onClick={handleExportCSV}
              disabled={filtered.length === 0}
              className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#C8A96E]/50 hover:bg-amber-50 disabled:opacity-40 text-slate-600 text-xs font-medium px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line"></i>
              Export CSV
            </button>
            {selectedCount > 0 && (
              <button
                onClick={handleMarkReviewed}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  selectedCount === reviewedCount
                    ? "bg-green-50 text-green-600 border border-green-200"
                    : "bg-white border border-slate-200 hover:border-[#C8A96E]/50 hover:bg-amber-50 text-slate-600"
                }`}
              >
                <i className={selectedCount === reviewedCount ? "ri-check-double-line" : "ri-eye-line"}></i>
                {selectedCount === reviewedCount ? "All Reviewed" : "Mark Reviewed"}
              </button>
            )}
          </div>
        </div>

        {/* Inner tabs */}
        <div className="px-6 py-2 border-b border-slate-200 shrink-0 flex items-center gap-1 bg-white">
          <TabButton active={tab === "all"} label="All" badge={totalCount} onClick={() => setTab("all")} />
          <TabButton active={tab === "selected"} label="Selected" badge={selectedCount} onClick={() => setTab("selected")} />
          <TabButton active={tab === "favorites"} label="Favorites" badge={favoriteCount} onClick={() => setTab("favorites")} />
          <TabButton active={tab === "notes"} label="Notes" badge={notesCount} onClick={() => setTab("notes")} />
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          <ModuleGate moduleId="photo_selection" moduleName="Photo Selection">
            <div className="px-6 py-5 space-y-5">
              {/* Add photo row */}
              <AddPhotoRow onAdd={handleAdd} saving={adding} tenantId={tenantId} jobId={job.id} />

              {/* Content */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <i className="ri-image-2-line text-2xl"></i>
                  </div>
                  <p className="text-slate-500 text-sm">
                    {photos.length === 0
                      ? "No photos yet — add the first one above"
                      : tab === "selected"
                        ? "No photos selected yet by client"
                        : tab === "favorites"
                          ? "No favorites yet"
                          : tab === "notes"
                            ? "No notes from client yet"
                            : "No photos match this filter"}
                  </p>
                </div>
              ) : tab === "notes" ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-amber-50/60 border border-amber-200/50 rounded-lg px-4 py-2.5 text-xs text-amber-700">
                    <i className="ri-message-3-line"></i>
                    <span>
                      <strong>{notesCount}</strong> note(s) from client —{" "}
                      <strong>{selectedWithNotes.length}</strong> of them on selected photos
                    </span>
                  </div>
                  {filtered.map((photo) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      index={0}
                      total={0}
                      mode="note"
                      onToggleSelected={handleToggleSelected}
                      onToggleFavorite={handleToggleFavorite}
                      onDelete={handleDelete}
                      onMoveUp={() => {}}
                      onMoveDown={() => {}}
                      onSaveEdit={handleSaveEdit}
                      disabled={busyId === photo.id}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filtered.map((photo, idx) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      index={idx}
                      total={filtered.length}
                      mode="grid"
                      onToggleSelected={handleToggleSelected}
                      onToggleFavorite={handleToggleFavorite}
                      onDelete={handleDelete}
                      onMoveUp={(p) => handleMove(p, "up")}
                      onMoveDown={(p) => handleMove(p, "down")}
                      onSaveEdit={handleSaveEdit}
                      disabled={busyId === photo.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </ModuleGate>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}